import React, { useState, useEffect, useRef, useCallback } from 'react';
import { auth, db } from '../../firebase/firebase';
import { 
  collection, doc, getDoc, getDocs, addDoc, query,
  orderBy, where, limit, startAfter, serverTimestamp,
  updateDoc, arrayUnion, arrayRemove, deleteDoc, onSnapshot
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  FiMoreVertical, FiTrash2, FiEdit, FiX, FiImage, FiSend
} from 'react-icons/fi';
import { 
  FaHeart, FaRegComment, FaShareSquare,
  FaRegLaughSquint, FaRegSadTear, FaRegSurprise
} from 'react-icons/fa';
import { BiLike, BiSolidLike } from 'react-icons/bi';
import { IoMdSend } from 'react-icons/io';
import ImageUploader from '../../components/ImageKit/PostImages';
import './Explore.css';
import { useNavigate } from 'react-router-dom';

const DEFAULT_AVATAR = '/default-profile.png';

const Explore = () => {
  const [currentUser, setCurrentUser]         = useState(null);
  const [userProfile, setUserProfile]         = useState(null);
  const [posts, setPosts]                     = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [newPostText, setNewPostText]         = useState('');
  const [imageUrl, setImageUrl]               = useState('');
  const [followingUsers, setFollowingUsers]   = useState([]);
  const [activeTab, setActiveTab]             = useState('following');
  const [commentText, setCommentText]         = useState('');
  const [activeCommentPost, setActiveCommentPost] = useState(null);
  const [editingPostId, setEditingPostId]     = useState(null);
  const [editPostText, setEditPostText]       = useState('');
  const [showReportDialog, setShowReportDialog] = useState(null);
  const [comments, setComments]               = useState({});
  const [lastVisible, setLastVisible]         = useState(null);
  const [hasMorePosts, setHasMorePosts]       = useState(true);
  const [showReactions, setShowReactions]     = useState(null);
  const [sharePostId, setSharePostId]         = useState(null);
  const [showOptionsMenu, setShowOptionsMenu] = useState(null);
  const [postImagePreview, setPostImagePreview] = useState('');

  const commentRef  = useRef(null);
  const observer    = useRef();
  const navigate    = useNavigate();

  const reactions = [
    { name: 'like',  icon: <BiLike size={20}/>,           activeIcon: <BiSolidLike size={20}/>,       color: '#1877f2', label: 'Like'    },
    { name: 'love',  icon: <FaHeart size={17}/>,          activeIcon: <FaHeart size={17}/>,           color: '#f3425f', label: 'Love'    },
    { name: 'laugh', icon: <FaRegLaughSquint size={17}/>, activeIcon: <FaRegLaughSquint size={17}/>,  color: '#f7b928', label: 'Haha'    },
    { name: 'sad',   icon: <FaRegSadTear size={17}/>,     activeIcon: <FaRegSadTear size={17}/>,      color: '#f7b928', label: 'Sad'     },
    { name: 'wow',   icon: <FaRegSurprise size={17}/>,    activeIcon: <FaRegSurprise size={17}/>,     color: '#f7b928', label: 'Wow'     },
  ];

  // Close menus on outside click
  useEffect(() => {
    const handler = () => { setShowOptionsMenu(null); setShowReactions(null); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Remove dark mode
  useEffect(() => {
    document.documentElement.removeAttribute('data-theme');
    localStorage.removeItem('darkMode');
  }, []);

  // Infinite scroll
  const lastPostRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMorePosts) loadMorePosts();
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMorePosts]);

  // Auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        await fetchFollowingUsers(user.uid);
        const uDoc = await getDoc(doc(db, 'users', user.uid));
        if (uDoc.exists()) setUserProfile(uDoc.data());
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const fetchFollowingUsers = async (userId) => {
    try {
      const snap = await getDocs(collection(db, 'users', userId, 'following'));
      setFollowingUsers(snap.docs.map(d => d.id));
    } catch (e) { console.error(e); }
  };

  const fetchPosts = useCallback(async (userId) => {
    try {
      const LIMIT = 10;
      let q;
      if (activeTab === 'following' && followingUsers.length > 0) {
        q = query(collection(db, 'posts'),
          where('userId', 'in', [...followingUsers, userId]),
          orderBy('timestamp', 'desc'), limit(LIMIT));
      } else {
        q = query(collection(db, 'posts'), orderBy('timestamp', 'desc'), limit(LIMIT));
      }

      const unsub = onSnapshot(q, async (snap) => {
        if (snap.empty) { setHasMorePosts(false); return; }
        const data = await Promise.all(snap.docs.map(async (d) => {
          const post = d.data();
          const userReactions = {};
          reactions.forEach(r => {
            userReactions[r.name] = post[`${r.name}s`]?.includes(userId) || false;
          });
          return { id: d.id, ...post, ...userReactions,
            commentsCount: post.commentsCount || 0,
            timestamp: post.timestamp?.toDate() || new Date() };
        }));
        setPosts(data);
        setLastVisible(snap.docs[snap.docs.length - 1]);
        setHasMorePosts(snap.docs.length === LIMIT);
      });
      return () => unsub();
    } catch (e) { console.error(e); setHasMorePosts(false); return () => {}; }
  }, [activeTab, followingUsers]);

  useEffect(() => {
    let unsub = () => {};
    if (currentUser) unsub = fetchPosts(currentUser.uid);
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [currentUser, activeTab, fetchPosts]);

  const loadMorePosts = async () => {
    if (!lastVisible || !hasMorePosts || !currentUser) return;
    setLoading(true);
    try {
      const LIMIT = 10;
      let q;
      if (activeTab === 'following' && followingUsers.length > 0) {
        q = query(collection(db, 'posts'),
          where('userId', 'in', [...followingUsers, currentUser.uid]),
          orderBy('timestamp', 'desc'), startAfter(lastVisible), limit(LIMIT));
      } else {
        q = query(collection(db, 'posts'), orderBy('timestamp', 'desc'),
          startAfter(lastVisible), limit(LIMIT));
      }
      const snap = await getDocs(q);
      if (snap.empty) { setHasMorePosts(false); return; }
      const newPosts = await Promise.all(snap.docs.map(async (d) => {
        const post = d.data();
        const userReactions = {};
        reactions.forEach(r => { userReactions[r.name] = post[`${r.name}s`]?.includes(currentUser.uid) || false; });
        return { id: d.id, ...post, ...userReactions,
          commentsCount: post.commentsCount || 0,
          timestamp: post.timestamp?.toDate() || new Date() };
      }));
      setPosts(prev => [...prev, ...newPosts]);
      setLastVisible(snap.docs[snap.docs.length - 1]);
      setHasMorePosts(snap.docs.length === LIMIT);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const createPost = async () => {
    if (!newPostText.trim() && !imageUrl) return;
    try {
      await addDoc(collection(db, 'posts'), {
        text: newPostText || 'Shared an image',
        imageUrl: imageUrl || null,
        userId: currentUser.uid,
        userRef: doc(db, 'users', currentUser.uid),
        userData: {
          name: userProfile?.Name || userProfile?.fullName || userProfile?.name || 'Farmer',
          username: userProfile?.username || '',
          profilePhoto: userProfile?.profilePhoto || '',
        },
        likes: [], loves: [], laughs: [], sads: [], wows: [],
        commentsCount: 0, reportsCount: 0,
        timestamp: serverTimestamp(),
      });
      setNewPostText(''); setImageUrl(''); setPostImagePreview('');
    } catch (e) { console.error(e); }
  };

  const handleReaction = async (postId, reactionType, alreadyReacted) => {
    if (!currentUser) return;
    try {
      const postRef = doc(db, 'posts', postId);
      const remove = {};
      reactions.forEach(r => {
        if (r.name !== reactionType) remove[`${r.name}s`] = arrayRemove(currentUser.uid);
      });
      if (alreadyReacted) {
        await updateDoc(postRef, { ...remove, [`${reactionType}s`]: arrayRemove(currentUser.uid) });
      } else {
        await updateDoc(postRef, { ...remove, [`${reactionType}s`]: arrayUnion(currentUser.uid) });
      }
    } catch (e) { console.error(e); }
  };

  const fetchComments = async (postId) => {
    try {
      const q = query(collection(db, 'posts', postId, 'comments'), orderBy('timestamp', 'asc'));
      const unsub = onSnapshot(q, (snap) => {
        setComments(prev => ({
          ...prev,
          [postId]: snap.docs.map(d => ({
            id: d.id, ...d.data(),
            timestamp: d.data().timestamp?.toDate() || new Date()
          }))
        }));
      });
      return () => unsub();
    } catch (e) { console.error(e); return () => {}; }
  };

  const addComment = async (postId) => {
    if (!commentText.trim() || !currentUser) return;
    try {
      await addDoc(collection(db, 'posts', postId, 'comments'), {
        text: commentText,
        userId: currentUser.uid,
        userRef: doc(db, 'users', currentUser.uid),
        userName: userProfile?.name || userProfile?.fullName || userProfile?.Name || 'Farmer',
        userProfile: userProfile?.profilePhoto || '',
        timestamp: serverTimestamp(),
      });
      await updateDoc(doc(db, 'posts', postId), {
        commentsCount: (posts.find(p => p.id === postId)?.commentsCount || 0) + 1
      });
      setCommentText('');
      setTimeout(() => { if (commentRef.current) commentRef.current.scrollTop = commentRef.current.scrollHeight; }, 100);
    } catch (e) { console.error(e); }
  };

  const reportPost = async (postId, reason) => {
    if (!currentUser) return;
    try {
      const reportQ = query(collection(db, 'posts', postId, 'reports'), where('userId', '==', currentUser.uid));
      const snap = await getDocs(reportQ);
      if (!snap.empty) { alert('You have already reported this post.'); return; }
      await addDoc(collection(db, 'posts', postId, 'reports'), {
        userId: currentUser.uid, reason, timestamp: serverTimestamp()
      });
      const current = (posts.find(p => p.id === postId)?.reportsCount || 0) + 1;
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, { reportsCount: current });
      if (current >= 10) await updateDoc(postRef, { hidden: true });
      setShowReportDialog(null);
      alert('Post reported. Thank you.');
    } catch (e) { console.error(e); }
  };

  const deletePost = async (postId) => {
    if (!currentUser) return;
    try {
      const d = await getDoc(doc(db, 'posts', postId));
      if (d.exists() && d.data().userId === currentUser.uid) await deleteDoc(doc(db, 'posts', postId));
    } catch (e) { console.error(e); }
  };

  const sharePost = async (postId) => {
    if (!currentUser) return;
    try {
      const postDoc = await getDoc(doc(db, 'posts', postId));
      if (!postDoc.exists()) return;
      const post = postDoc.data();
      await addDoc(collection(db, 'posts'), {
        text: `Shared post: ${post.text || ''}`,
        imageUrl: post.imageUrl || null,
        originalPostId: postId,
        userId: currentUser.uid,
        userRef: doc(db, 'users', currentUser.uid),
        userData: {
          name: userProfile?.name || userProfile?.fullName || 'Farmer',
          username: userProfile?.username || '',
          profilePhoto: userProfile?.profilePhoto || '',
        },
        likes: [], loves: [], laughs: [], sads: [], wows: [],
        commentsCount: 0, timestamp: serverTimestamp(),
      });
      setSharePostId(null);
      alert('Post shared!');
    } catch (e) { console.error(e); }
  };

  const saveEditedPost = async (postId) => {
    if (!editPostText.trim()) return;
    try {
      await updateDoc(doc(db, 'posts', postId), { text: editPostText });
      setEditingPostId(null); setEditPostText('');
    } catch (e) { console.error(e); }
  };

  const toggleComments = async (postId) => {
    if (activeCommentPost === postId) { setActiveCommentPost(null); return; }
    setActiveCommentPost(postId);
    if (!comments[postId]) await fetchComments(postId);
  };

  const getTotalReactions = (post) =>
    reactions.reduce((t, r) => t + (post[`${r.name}s`]?.length || 0), 0);

  const getUserReaction = (post) =>
    reactions.find(r => post[r.name]) || null;

  const formatDate = (date) => {
    if (!date) return '';
    const diff = Math.floor((new Date() - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading && posts.length === 0) {
    return (
      <div className="ex-loading-screen">
        <div className="ex-spinner" />
      </div>
    );
  }

  const myReactionColor = (post) => {
    const r = getUserReaction(post);
    return r ? r.color : 'inherit';
  };

  return (
    <div className="ex-root">
      {/* ── LEFT SIDEBAR ── */}
      <aside className="ex-sidebar">
        {currentUser && userProfile && (
          <div className="ex-sidebar-profile" onClick={() => navigate(`/profile/${currentUser.uid}`)}>
            <img src={userProfile.profilePhoto || DEFAULT_AVATAR} alt="me" className="ex-sidebar-avatar" />
            <div>
              <p className="ex-sidebar-name">{userProfile.name || userProfile.fullName || userProfile.Name || 'Farmer'}</p>
              <p className="ex-sidebar-username">@{userProfile.username || ''}</p>
            </div>
          </div>
        )}
        <nav className="ex-sidebar-nav">
          <button className={`ex-nav-item ${activeTab === 'following' ? 'active' : ''}`} onClick={() => setActiveTab('following')}>
            <span className="ex-nav-icon">🌾</span> Following
          </button>
          <button className={`ex-nav-item ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
            <span className="ex-nav-icon">🌍</span> Discover
          </button>
        </nav>
        <div className="ex-sidebar-tip">
          <p>🌱 Ankurit Community</p>
          <span>Connect with farmers across India</span>
        </div>
      </aside>

      {/* ── FEED ── */}
      <main className="ex-feed">

        {/* Create Post Card */}
        {currentUser && (
          <div className="ex-create-card">
            <div className="ex-create-top">
              <img src={userProfile?.profilePhoto || DEFAULT_AVATAR} alt="me" className="ex-avatar-sm" />
              <input
                className="ex-create-input"
                placeholder={`What's growing, ${userProfile?.name || userProfile?.Name || 'Farmer'}?`}
                value={newPostText}
                onChange={e => setNewPostText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && createPost()}
              />
            </div>

            {(imageUrl || postImagePreview) && (
              <div className="ex-create-preview">
                <img src={imageUrl || postImagePreview} alt="preview" />
                <button className="ex-create-remove-img" onClick={() => { setImageUrl(''); setPostImagePreview(''); }}>
                  <FiX />
                </button>
              </div>
            )}

            <div className="ex-create-actions">
              <div className="ex-create-left">
                <ImageUploader
                  userId={currentUser.uid}
                  type="post"
                  onUploadComplete={url => { setImageUrl(url); setPostImagePreview(url); }}
                  label="Photo"
                  required={false}
                />
              </div>
              <button
                className="ex-post-btn"
                onClick={createPost}
                disabled={!newPostText.trim() && !imageUrl}
              >
                Post
              </button>
            </div>
          </div>
        )}

        {/* Posts */}
        {posts.length === 0 && !loading ? (
          <div className="ex-empty">
            <span>🌾</span>
            <p>{activeTab === 'following'
              ? "Follow some farmers to see their posts here!"
              : "No posts yet — be the first to share!"}</p>
          </div>
        ) : (
          posts.map((post, idx) => {
            const userReaction = getUserReaction(post);
            const totalReactions = getTotalReactions(post);

            return (
              <div
                key={post.id}
                className={`ex-post-card ${post.hidden ? 'ex-post-hidden' : ''}`}
                ref={idx === posts.length - 1 ? lastPostRef : null}
              >
                {post.hidden ? (
                  <p className="ex-hidden-msg">This post was hidden due to multiple reports.</p>
                ) : (
                  <>
                    {/* Post Header */}
                    <div className="ex-post-header">
                      <div className="ex-post-author" onClick={() => navigate(`/profile/${post.userId}`)}>
                        <img src={post.userData?.profilePhoto || DEFAULT_AVATAR} alt="author" className="ex-avatar-sm" />
                        <div>
                          <p className="ex-author-name">{post.userData?.fullName || post.userData?.name || 'Farmer'}</p>
                          <p className="ex-author-meta">
                            @{post.userData?.username || 'user'} · {formatDate(post.timestamp)}
                            {post.originalPostId && <span className="ex-shared-badge"> · Shared</span>}
                          </p>
                        </div>
                      </div>

                      <div className="ex-post-menu-wrap" onClick={e => e.stopPropagation()}>
                        {currentUser?.uid === post.userId ? (
                          <div className="ex-post-owner-actions">
                            <button className="ex-icon-btn" title="Edit" onClick={() => { setEditingPostId(post.id); setEditPostText(post.text); }}>
                              <FiEdit size={16} />
                            </button>
                            <button className="ex-icon-btn ex-icon-danger" title="Delete" onClick={() => deletePost(post.id)}>
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <button className="ex-icon-btn" onClick={() => setShowOptionsMenu(showOptionsMenu === post.id ? null : post.id)}>
                              <FiMoreVertical size={18} />
                            </button>
                            {showOptionsMenu === post.id && (
                              <div className="ex-dropdown">
                                <button onClick={() => { setShowReportDialog(post.id); setShowOptionsMenu(null); }}>
                                  🚩 Report post
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {/* Post Body */}
                    {editingPostId === post.id ? (
                      <div className="ex-edit-wrap">
                        <textarea
                          value={editPostText}
                          onChange={e => setEditPostText(e.target.value)}
                          rows={3}
                          className="ex-edit-textarea"
                        />
                        <div className="ex-edit-actions">
                          <button className="ex-btn-ghost" onClick={() => setEditingPostId(null)}>Cancel</button>
                          <button className="ex-btn-primary" onClick={() => saveEditedPost(post.id)}>Save</button>
                        </div>
                      </div>
                    ) : (
                      <div className="ex-post-body">
                        {post.text && <p className="ex-post-text">{post.text}</p>}
                        {post.imageUrl && (
                          <div className="ex-post-image-wrap">
                            <img src={post.imageUrl} alt="Post" className="ex-post-image" />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Reaction counts */}
                    {(totalReactions > 0 || post.commentsCount > 0) && (
                      <div className="ex-post-stats">
                        {totalReactions > 0 && (
                          <span className="ex-stat-reactions">
                            {reactions.filter(r => (post[`${r.name}s`]?.length || 0) > 0).map(r => (
                              <span key={r.name} style={{ color: r.color }}>{r.activeIcon}</span>
                            ))}
                            {totalReactions}
                          </span>
                        )}
                        {post.commentsCount > 0 && (
                          <span className="ex-stat-comments" onClick={() => toggleComments(post.id)}>
                            {post.commentsCount} comment{post.commentsCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Action Bar */}
                    <div className="ex-action-bar">
                      {/* Like / Reactions */}
                      <div
                        className="ex-reaction-wrap"
                        onMouseEnter={() => setShowReactions(post.id)}
                        onMouseLeave={() => setShowReactions(null)}
                        onClick={e => e.stopPropagation()}
                      >
                        <button
                          className={`ex-action-btn ${userReaction ? 'ex-reacted' : ''}`}
                          style={{ color: userReaction ? userReaction.color : undefined }}
                          onClick={() => handleReaction(post.id, userReaction?.name || 'like', !!userReaction)}
                        >
                          {userReaction ? userReaction.activeIcon : <BiLike size={18} />}
                          <span>{userReaction ? userReaction.label : 'Like'}</span>
                        </button>

                        {showReactions === post.id && (
                          <div className="ex-reactions-popup">
                            {reactions.map(r => (
                              <button
                                key={r.name}
                                className="ex-reaction-pill"
                                style={{ '--rc': r.color }}
                                title={r.label}
                                onClick={() => { handleReaction(post.id, r.name, post[r.name]); setShowReactions(null); }}
                              >
                                <span className={post[r.name] ? 'ex-r-active' : ''}>{r.icon}</span>
                                <span className="ex-r-label">{r.label}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <button
                        className={`ex-action-btn ${activeCommentPost === post.id ? 'ex-reacted' : ''}`}
                        onClick={() => toggleComments(post.id)}
                      >
                        <FaRegComment size={17} />
                        <span>Comment</span>
                      </button>

                      <button
                        className="ex-action-btn"
                        onClick={() => setSharePostId(sharePostId === post.id ? null : post.id)}
                      >
                        <FaShareSquare size={17} />
                        <span>Share</span>
                      </button>
                    </div>

                    {/* Share confirm */}
                    {sharePostId === post.id && (
                      <div className="ex-share-confirm">
                        <p>Share this post to your timeline?</p>
                        <div>
                          <button className="ex-btn-ghost" onClick={() => setSharePostId(null)}>Cancel</button>
                          <button className="ex-btn-primary" onClick={() => sharePost(post.id)}>Share now</button>
                        </div>
                      </div>
                    )}

                    {/* Comments Section */}
                    {activeCommentPost === post.id && (
                      <div className="ex-comments-wrap">
                        <div className="ex-comments-list" ref={commentRef}>
                          {(comments[post.id] || []).length === 0 ? (
                            <p className="ex-no-comments">Be the first to comment!</p>
                          ) : (
                            (comments[post.id] || []).map(c => (
                              <div key={c.id} className="ex-comment">
                                <img src={c.userProfile || DEFAULT_AVATAR} alt={c.userName} className="ex-avatar-xs" />
                                <div className="ex-comment-bubble">
                                  <div className="ex-comment-meta">
                                    <span className="ex-comment-name">{c.userName}</span>
                                    <span className="ex-comment-time">{formatDate(c.timestamp)}</span>
                                  </div>
                                  <p className="ex-comment-text">{c.text}</p>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                        <div className="ex-add-comment">
                          <img src={userProfile?.profilePhoto || DEFAULT_AVATAR} alt="me" className="ex-avatar-xs" />
                          <div className="ex-comment-input-row">
                            <input
                              type="text"
                              placeholder="Write a comment…"
                              value={commentText}
                              onChange={e => setCommentText(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && addComment(post.id)}
                            />
                            <button
                              className="ex-comment-send"
                              onClick={() => addComment(post.id)}
                              disabled={!commentText.trim()}
                            >
                              <IoMdSend size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Report Dialog */}
                {showReportDialog === post.id && (
                  <div className="ex-report-overlay" onClick={() => setShowReportDialog(null)}>
                    <div className="ex-report-dialog" onClick={e => e.stopPropagation()}>
                      <div className="ex-report-header">
                        <h3>Report post</h3>
                        <button className="ex-icon-btn" onClick={() => setShowReportDialog(null)}><FiX /></button>
                      </div>
                      <p>Why are you reporting this post?</p>
                      <div className="ex-report-options">
                        {['Spam', 'Inappropriate content', 'Misinformation', 'Harassment', 'Other'].map(reason => (
                          <button key={reason} onClick={() => reportPost(post.id, reason)}>{reason}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}

        {loading && posts.length > 0 && (
          <div className="ex-loading-more">
            <div className="ex-spinner" />
          </div>
        )}
      </main>
    </div>
  );
};

export default Explore;
